// API client for Custom (Quality Adjustment) Templates — the production data source for
// useCustomTemplates.ts, replacing the templatesRepository.ts localStorage POC.
//
// Validation boundary: this module never rejects a payload itself (e.g. checking `specType`
// against `scope`) — the backend is the sole authority on correctness/integrity. It only
// forwards requests and re-surfaces whatever the backend responds with, including validation
// errors, so callers (and showErrorNotification) see the backend's own message.

import type {
  ITemplate,
  ITemplateCreate,
  ITemplateListQuery,
  ITemplateListResponse,
  ITemplateUpdate,
  SpecType,
} from '@local/lib';

import { isAxiosError } from 'axios';

import { apiClient } from './apiClient';

// Every template is scoped to exactly one Quality Specification — `'*'` denotes "no scope" (i.e.
// every spec), used by list/search across all templates regardless of which spec they belong to
// (see `useAllCustomTemplates` in `pages/templates/hooks/useCustomTemplates.ts`).
export type TemplateScope = '*' | SpecType;

/** Error body shape returned by the API's `buildPublicLambdaErrorResponse` (services/api). */
interface IApiErrorBody {
  error?: string;
  message?: string;
}

/**
 * Surfaces the backend's own error message (including Zod validation errors) instead of axios's
 * generic "Request failed with status code 4xx/5xx" — the backend is the validation authority,
 * so its message is what callers and `showErrorNotification` should display.
 */
const toApiError = (error: unknown): Error => {
  if (isAxiosError(error)) {
    const body = error.response?.data as IApiErrorBody | undefined;
    return new Error(body?.message ?? body?.error ?? error.message, { cause: error });
  }
  return error instanceof Error ? error : new Error('Request failed', { cause: error });
};

/** Appends `specType` for a concrete scope; `'*'` (no scope) omits the filter entirely. */
const appendScopeParam = (params: URLSearchParams, scope: TemplateScope): void => {
  if (scope !== '*') params.append('specType', scope);
};

/**
 * Fetches templates, optionally scoped to a single Quality Specification.
 *
 * Supports `AbortSignal` for query cancellation — TanStack Query supplies this automatically.
 *
 * @param scope - Quality Specification to filter by, or `'*'` for every spec
 * @param filters - Optional pagination filters
 * @param signal - Optional abort signal for cancellation
 * @throws Error with the backend's own message if the request fails
 */
export const fetchCustomTemplates = async (
  scope: TemplateScope,
  filters: Partial<ITemplateListQuery> = {},
  signal?: AbortSignal,
): Promise<ITemplateListResponse> => {
  const queryParams = new URLSearchParams();
  appendScopeParam(queryParams, scope);

  if (filters.limit !== undefined) {
    queryParams.append('limit', filters.limit.toString());
  }
  if (filters.nextToken) {
    queryParams.append('nextToken', filters.nextToken);
  }

  const queryString = queryParams.toString();
  try {
    const { data } = await apiClient.get<ITemplateListResponse>(`/templates${queryString ? `?${queryString}` : ''}`, {
      signal,
    });
    return data;
  } catch (error) {
    throw toApiError(error);
  }
};

/**
 * Fetches a single template by id.
 *
 * `scope` is sent as a query filter (not required for lookup, since ids are globally unique) so
 * the backend can authoritatively reject a template outside the expected Quality Specification —
 * this client never makes that decision itself.
 *
 * @param scope - Expected Quality Specification, or `'*'` to skip that check
 * @param templateId - The template id to fetch
 * @param signal - Optional abort signal for cancellation
 * @throws Error with the backend's own message if the request fails
 */
export const fetchCustomTemplateById = async (
  scope: TemplateScope,
  templateId: string,
  signal?: AbortSignal,
): Promise<ITemplate> => {
  const queryParams = new URLSearchParams();
  appendScopeParam(queryParams, scope);
  const queryString = queryParams.toString();

  try {
    const { data } = await apiClient.get<ITemplate>(`/templates/${templateId}${queryString ? `?${queryString}` : ''}`, {
      signal,
    });
    return data;
  } catch (error) {
    throw toApiError(error);
  }
};

/**
 * Creates a new template. `scope` is forwarded as a query filter purely so the backend can
 * enforce — not this client — that it agrees with `data.specType` (see `templatesApiHandler.ts`).
 *
 * @throws Error with the backend's own message if the request fails (including validation errors)
 */
export const createCustomTemplate = async (scope: TemplateScope, data: ITemplateCreate): Promise<ITemplate> => {
  const queryParams = new URLSearchParams();
  appendScopeParam(queryParams, scope);
  const queryString = queryParams.toString();

  try {
    const { data: created } = await apiClient.post<ITemplate>(
      `/templates${queryString ? `?${queryString}` : ''}`,
      data,
    );
    return created;
  } catch (error) {
    throw toApiError(error);
  }
};

/**
 * Updates an existing template. `scope` is forwarded as a query filter purely so the backend can
 * enforce — not this client — that it agrees with `data.specType` (see `templatesApiHandler.ts`).
 *
 * @throws Error with the backend's own message if the request fails (including validation errors)
 */
export const updateCustomTemplate = async (
  scope: TemplateScope,
  templateId: string,
  data: ITemplateUpdate,
): Promise<ITemplate> => {
  const queryParams = new URLSearchParams();
  appendScopeParam(queryParams, scope);
  const queryString = queryParams.toString();

  try {
    const { data: updated } = await apiClient.put<ITemplate>(
      `/templates/${templateId}${queryString ? `?${queryString}` : ''}`,
      data,
    );
    return updated;
  } catch (error) {
    throw toApiError(error);
  }
};

/**
 * Deletes a template by id. `scope` is sent as a query filter so the backend can authoritatively
 * reject deleting a template outside the expected scope.
 *
 * @throws Error with the backend's own message if the request fails
 */
export const deleteCustomTemplate = async (scope: TemplateScope, templateId: string): Promise<void> => {
  const queryParams = new URLSearchParams();
  appendScopeParam(queryParams, scope);
  const queryString = queryParams.toString();

  try {
    await apiClient.delete(`/templates/${templateId}${queryString ? `?${queryString}` : ''}`);
  } catch (error) {
    throw toApiError(error);
  }
};
